import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Button,
  Tooltip,
} from '@mui/material';
import { Dispatch, FC, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Scrollbar } from 'src/components/atoms/scrollbar';
import { TruncatedTextWithChildren } from 'src/components/atoms/truncated-text-with-children';

import { PickedClause } from '../../templates/gdpr';

import { Gdpr } from '@/features/settings/api/types/gdpr';
import { Can } from '@/permissions/can';

interface Props {
  data: Gdpr[];
  setPickedClause: Dispatch<SetStateAction<PickedClause | null>>;
  handleOpenRemoveGdprClauseDialog: () => void;
  handleOpenUpdateClauseDialog: () => void;
  handleOpenShowAllClauseDialog: () => void;
}

export const GdprClausesTable: FC<Props> = ({
  data,
  setPickedClause,
  handleOpenRemoveGdprClauseDialog,
  handleOpenUpdateClauseDialog,
  handleOpenShowAllClauseDialog,
}) => {
  const { t } = useTranslation(['settings', 'common']);

  const columns = [
    {
      id: 'language',
      label: t('settings:gdpr.table.language'),
    },
    {
      id: 'content',
      label: t('settings:gdpr.table.content'),
    },
    {
      id: 'actions',
      label: '',
    },
  ];

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
      }}>
      <Scrollbar>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {columns.map(column => (
                <TableCell key={column.id}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map(({ id, language, gdprClause, type }, index) => (
              <TableRow key={`${index}-${type}`}>
                <TableCell>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                    }}>
                    <img src={`/static/${language}_flag.svg`} alt="language flag" width={20} />
                    <Typography color="inherit" variant="inherit">
                      {t(`settings:gdpr.${language}`)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="inherit">
                    {!gdprClause ? (
                      t('settings:gdpr.noClause')
                    ) : (
                      <TruncatedTextWithChildren text={gdprClause ?? ''}>
                        <Button
                          variant="text"
                          onClick={() => {
                            setPickedClause({ type, language });
                            handleOpenShowAllClauseDialog();
                          }}>
                          {t('settings:gdpr.showAll')}
                        </Button>
                      </TruncatedTextWithChildren>
                    )}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    {id ? (
                      <Can I="update" a="GDPR" passThrough>
                        {allowed => (
                          <Tooltip
                            title={t('common:notSufficientPermissions')}
                            disableHoverListener={allowed}
                            disableFocusListener={allowed}>
                            <span>
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setPickedClause({ type, language });
                                  handleOpenUpdateClauseDialog();
                                }}
                                disabled={!allowed}>
                                {t('common:button.edit')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Can>
                    ) : (
                      <Can I="create" a="GDPR" passThrough>
                        {allowed => (
                          <Tooltip
                            title={t('common:notSufficientPermissions')}
                            disableHoverListener={allowed}
                            disableFocusListener={allowed}>
                            <span>
                              <Button
                                variant="contained"
                                onClick={() => {
                                  setPickedClause({ type, language });
                                  handleOpenUpdateClauseDialog();
                                }}
                                disabled={!allowed}>
                                {t('common:button.add')}
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Can>
                    )}

                    <Can I="delete" a="GDPR" passThrough>
                      {allowed => (
                        <Tooltip
                          title={t('common:notSufficientPermissions')}
                          disableHoverListener={allowed}
                          disableFocusListener={allowed}>
                          <span>
                            <Button
                              variant="outlined"
                              disabled={!language || !allowed}
                              onClick={() => {
                                setPickedClause({ type, language });
                                handleOpenRemoveGdprClauseDialog();
                              }}>
                              {t('common:button.remove')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </Can>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </Box>
  );
};
